<template>
  <TsMediaStart append-class="md:mr-2 max-md:max-w-max max-md:flex items-start">
    <div class="p-2 max-md:mr-4 bg-white rounded-md shrink-0">
      <TsImage
        v-if="product && product.image"
        height="64"
        width="64"
        :src="product.image"
        :alt="product.code"
        :data-testid="props.dataTestid + '-trolley-item-' + product.code + '-image'"
        loading="lazy"
      />
    </div>
    <TsTypography v-if="isMobile" variant="h5" append-class="mb-0 line-clamp-[4]">
      <span
        class="text-idle-black"
        :data-testid="props.dataTestid + '-trolley-item-' + product.code + '-name'"
        >{{ product?.name }}
      </span>
    </TsTypography>
  </TsMediaStart>
  <TsMediaContent append-class="p-1 lg:p-2">
    <TsTypography v-if="!isMobile" variant="h5" append-class="mb-0 line-clamp-2">
      <span
        class="text-idle-black"
        :data-testid="props.dataTestid + '-trolley-item-' + product.code + '-name'"
        >{{ product?.name }}
      </span>
    </TsTypography>
    <div class="flex justify-between gap-2 lg:gap-20 md:mt-2">
      <div>
        <TsTypography append-class="text-sm md:text-base mb-1"
          >{{ useTranslation('id', 'Productcode') }}
        </TsTypography>
        <TsTypography
          append-class="text-sm md:text-base font-semibold text-idle-black"
          :data-testid="props.dataTestid + '-trolley-item-' + product.code + '-code'"
          >{{ product?.code }}
        </TsTypography>
      </div>
      <div>
        <TsTypography
          append-class="text-sm md:text-base mb-1"
          :data-testid="props.dataTestid + '-trolley-item-quantity-text'"
          >{{ useTranslation('quantity', 'Hoeveelheid') }}
        </TsTypography>
        <TsTypography
          append-class="text-sm md:text-base font-semibold text-idle-black"
          :data-testid="props.dataTestid + '-trolley-item-' + product.code + '-quantity'"
          >{{ quantity }}
        </TsTypography>
      </div>
      <div class="md:ms-auto max-md:mt-auto">
        <TsTypography
          v-if="primaryPrice"
          weight="bold"
          append-class="text-idle-black leading-[18px] max-md:flex-row-reverse flex items-end gap-1 justify-start justify-end"
          :data-testid="props.dataTestid + '-trolley-item-' + product.code + '-vat-primary-price'"
        >
          <TsTypography
            v-if="product.prices && product.prices.formatted.per_unit && appRegion === 'BE'"
            as="span"
            variant="xs"
            append-class="text-natural-silver-grey leading-tight"
            :data-testid="`product-${props.product?.code}-formatted-per-unit-price`"
          >
            {{ product.prices.formatted.per_unit }}
          </TsTypography>
          <span>€ {{ primaryPrice }}</span>
        </TsTypography>
        <TsTypography
          v-if="secondaryPrice"
          append-class="text-right"
          variant="xs"
          :data-testid="props.dataTestid + '-trolley-item-' + product.code + '-vat-secondary-price'"
        >
          {{
            rootStore.isIncVat
              ? 'Excl. ' + useTranslation('vat', 'btw')
              : 'Incl. ' + useTranslation('vat', 'btw')
          }}
          € {{ secondaryPrice }}
        </TsTypography>
      </div>
    </div>
  </TsMediaContent>
</template>

<script lang="ts" setup>
import type { Product } from '~/types/ecom/product/product.type';
import type { TrolleyChannel } from '~/types/ecom/trolley/trolley-map';
import { useTranslation } from '~/composables/useTranslation';

type Props = {
  product: Product;
  quantity: number;
  channel?: keyof typeof TrolleyChannel;
  dataTestid: string;
  appendClass?: string;
};

const TsMediaStart = defineAsyncComponent(() => import('~/components/organisms/TsMediaStart.vue'));

const TsMediaContent = defineAsyncComponent(
  () => import('~/components/organisms/TsMediaContent.vue')
);

const { isMobile } = useDevice();

const props = withDefaults(defineProps<Props>(), {
  appendClass: '',
  channel: 'Collection',
});

const rootStore = useRootStore();
const config = useRuntimeConfig();
const appRegion = config.public.appRegion;

const formatted_total_amount_gross = computed<string>(() => {
  if (!props.product || !props.product.prices) return '';
  return (Number(props.product.prices.raw.gross) * props.quantity).toFixed(2).replace('.', ',');
});

const formatted_total_amount_net = computed<string>(() => {
  if (!props.product || !props.product.prices) return '';
  return (Number(props.product.prices.raw.net) * props.quantity).toFixed(2).replace('.', ',');
});

const secondaryPrice = computed<string>(() =>
  rootStore.isIncVat ? formatted_total_amount_net.value : formatted_total_amount_gross.value
);

const primaryPrice = computed<string>(() =>
  rootStore.isIncVat ? formatted_total_amount_gross.value : formatted_total_amount_net.value
);
</script>
